import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, CreditCard, Banknote, Plus, Check, ArrowRight, AlertCircle, Award, Gift, Tag, User, Phone, Clock } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { items, totalPrice, clearCart } = useCart();
  const { user, token, addAddress, refreshUser, loyaltyPoints } = useAuth();
  const { language } = useLanguage();
  
  // Check if guest checkout from QR menu
  const isGuestCheckout = searchParams.get('guest') === 'true';
  const tableNumber = searchParams.get('table');
  
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('nakit');
  const [usePoints, setUsePoints] = useState(false);
  const [restaurantClosed, setRestaurantClosed] = useState(false);
  const [closedMessage, setClosedMessage] = useState('');
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  // Check if restaurant is open
  useEffect(() => {
    axios.get(`${API}/settings/is-open`).then(res => {
      if (!res.data.is_open) {
        setRestaurantClosed(true);
        setClosedMessage(res.data.message);
      }
    }).catch(() => {});
  }, []);
  const [pointsToUse, setPointsToUse] = useState(0);
  
  // Guest info
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  
  // Coupon from cart
  const [appliedCoupon, setAppliedCoupon] = useState(location.state?.couponCode || null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  
  // Loyalty constants - Updated values
  const MIN_POINTS_TO_REDEEM = 50;
  const EURO_PER_POINT = 0.20;

  // Translations
  const texts = {
    tr: {
      pageTitle: 'Sipariş Tamamla',
      pageSubtitle: 'Teslimat bilgilerinizi kontrol edin',
      guestTitle: 'Masa Siparişi',
      guestSubtitle: 'Bilgilerinizi girin',
      tableOrder: 'Masa',
      guestInfo: 'Misafir Bilgileri',
      guestName: 'Adınız',
      guestNamePlaceholder: 'Adınızı girin',
      guestPhone: 'Telefon',
      guestPhonePlaceholder: '+382 ...',
      deliveryAddress: 'Teslimat Adresi',
      newAddress: 'Yeni Adres',
      addNewAddress: 'Yeni Adres Ekle',
      addressTitle: 'Adres Başlığı',
      addressTitlePlaceholder: 'Örn: Ev, İş',
      addressDetail: 'Adres',
      addressDetailPlaceholder: 'Sokak, apartman, daire no',
      district: 'İlçe',
      city: 'Şehir',
      saveAddress: 'Adresi Kaydet',
      default: 'Varsayılan',
      noAddress: 'Kayıtlı adresiniz yok. Yeni adres ekleyin.',
      paymentMethod: 'Ödeme Yöntemi',
      cashOnDelivery: 'Kapıda Nakit',
      cashDesc: 'Teslimat sırasında nakit ödeme',
      cardOnDelivery: 'Kapıda Kredi Kartı',
      cardDesc: 'Teslimat sırasında kart ile ödeme',
      payAtTable: 'Masada Ödeme',
      payAtTableDesc: 'Garson geldiğinde ödeme yapın',
      orderNote: 'Sipariş Notu (Opsiyonel)',
      orderNotePlaceholder: 'Varsa özel isteklerinizi yazın...',
      usePoints: 'Puan Kullan',
      pointsAvailable: 'puan mevcut',
      pointsToUse: 'Kullanılacak Puan',
      minPoints: 'Min:',
      pointsInfo: 'puan | 100 puan = €10 indirim',
      orderSummary: 'Sipariş Özeti',
      subtotal: 'Ara Toplam',
      couponDiscount: 'Kupon İndirimi',
      pointsDiscount: 'Puan İndirimi',
      delivery: 'Teslimat',
      free: 'Ücretsiz',
      total: 'Toplam',
      pointsEarned: 'Kazanılacak Puan',
      confirmOrder: 'Siparişi Onayla',
      fillAllFields: 'Lütfen tüm alanları doldurun',
      fillGuestInfo: 'Lütfen adınızı girin',
      addressAdded: 'Adres eklendi',
      addressError: 'Adres eklenirken hata oluştu',
      selectAddress: 'Lütfen bir teslimat adresi seçin',
      orderSuccess: 'Siparişiniz alındı!',
      orderError: 'Sipariş oluşturulurken hata oluştu',
      catering: 'Catering',
      alakart: 'Alakart'
    },
    en: {
      pageTitle: 'Checkout',
      pageSubtitle: 'Review your delivery information',
      guestTitle: 'Table Order',
      guestSubtitle: 'Enter your information',
      tableOrder: 'Table',
      guestInfo: 'Guest Information',
      guestName: 'Your Name',
      guestNamePlaceholder: 'Enter your name',
      guestPhone: 'Phone',
      guestPhonePlaceholder: '+382 ...',
      deliveryAddress: 'Delivery Address',
      newAddress: 'New Address',
      addNewAddress: 'Add New Address',
      addressTitle: 'Address Title',
      addressTitlePlaceholder: 'E.g.: Home, Work',
      addressDetail: 'Address',
      addressDetailPlaceholder: 'Street, building, apartment no',
      district: 'District',
      city: 'City',
      saveAddress: 'Save Address',
      default: 'Default',
      noAddress: 'No saved addresses. Add a new address.',
      paymentMethod: 'Payment Method',
      cashOnDelivery: 'Cash on Delivery',
      cashDesc: 'Pay with cash when delivered',
      cardOnDelivery: 'Card on Delivery',
      cardDesc: 'Pay with card when delivered',
      payAtTable: 'Pay at Table',
      payAtTableDesc: 'Pay when waiter arrives',
      orderNote: 'Order Note (Optional)',
      orderNotePlaceholder: 'Write any special requests...',
      usePoints: 'Use Points',
      pointsAvailable: 'points available',
      pointsToUse: 'Points to Use',
      minPoints: 'Min:',
      pointsInfo: 'points | 100 points = €10 discount',
      orderSummary: 'Order Summary',
      subtotal: 'Subtotal',
      couponDiscount: 'Coupon Discount',
      pointsDiscount: 'Points Discount',
      delivery: 'Delivery',
      free: 'Free',
      total: 'Total',
      pointsEarned: 'Points to Earn',
      confirmOrder: 'Confirm Order',
      fillAllFields: 'Please fill in all fields',
      fillGuestInfo: 'Please enter your name',
      addressAdded: 'Address added',
      addressError: 'Error adding address',
      selectAddress: 'Please select a delivery address',
      orderSuccess: 'Your order has been placed!',
      orderError: 'Error creating order',
      catering: 'Catering',
      alakart: 'A La Carte'
    },
    me: {
      pageTitle: 'Završi Narudžbu',
      pageSubtitle: 'Pregledajte podatke o dostavi',
      guestTitle: 'Narudžba sa Stola',
      guestSubtitle: 'Unesite vaše podatke',
      tableOrder: 'Sto',
      guestInfo: 'Podaci Gosta',
      guestName: 'Vaše Ime',
      guestNamePlaceholder: 'Unesite vaše ime',
      guestPhone: 'Telefon',
      guestPhonePlaceholder: '+382 ...',
      deliveryAddress: 'Adresa Dostave',
      newAddress: 'Nova Adresa',
      addNewAddress: 'Dodaj Novu Adresu',
      addressTitle: 'Naziv Adrese',
      addressTitlePlaceholder: 'Npr: Kuća, Posao',
      addressDetail: 'Adresa',
      addressDetailPlaceholder: 'Ulica, zgrada, broj stana',
      district: 'Opština',
      city: 'Grad',
      saveAddress: 'Sačuvaj Adresu',
      default: 'Podrazumijevano',
      noAddress: 'Nemate sačuvanih adresa. Dodajte novu adresu.',
      paymentMethod: 'Način Plaćanja',
      cashOnDelivery: 'Gotovina pri Dostavi',
      cashDesc: 'Platite gotovinom pri dostavi',
      cardOnDelivery: 'Kartica pri Dostavi',
      cardDesc: 'Platite karticom pri dostavi',
      payAtTable: 'Plaćanje za Stolom',
      payAtTableDesc: 'Platite kada konobar dođe',
      orderNote: 'Napomena (Opcionalno)',
      orderNotePlaceholder: 'Napišite posebne zahtjeve...',
      usePoints: 'Koristi Poene',
      pointsAvailable: 'poena dostupno',
      pointsToUse: 'Poeni za Korištenje',
      minPoints: 'Min:',
      pointsInfo: 'poena | 100 poena = €10 popusta',
      orderSummary: 'Pregled Narudžbe',
      subtotal: 'Međuzbir',
      couponDiscount: 'Popust Kupona',
      pointsDiscount: 'Popust Poena',
      delivery: 'Dostava',
      free: 'Besplatno',
      total: 'Ukupno',
      pointsEarned: 'Poeni za Zaradu',
      confirmOrder: 'Potvrdi Narudžbu',
      fillAllFields: 'Molimo popunite sva polja',
      fillGuestInfo: 'Molimo unesite vaše ime',
      addressAdded: 'Adresa dodana',
      addressError: 'Greška pri dodavanju adrese',
      selectAddress: 'Molimo odaberite adresu dostave',
      orderSuccess: 'Vaša narudžba je primljena!',
      orderError: 'Greška pri kreiranju narudžbe',
      catering: 'Ketering',
      alakart: 'A La Carte'
    }
  };

  const txt = texts[language] || texts.en;
  
  // Validate coupon on mount
  useEffect(() => {
    if (appliedCoupon) {
      validateCoupon(appliedCoupon);
    }
  }, [totalPrice]);
  
  const validateCoupon = async (code) => {
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: code,
        subtotal: totalPrice
      });
      setCouponDiscount(response.data.discount_amount);
    } catch {
      setAppliedCoupon(null);
      setCouponDiscount(0);
    }
  };
  
  // Set default address when user addresses change
  useEffect(() => {
    if (user?.addresses?.length > 0 && !selectedAddress) {
      const defaultAddr = user.addresses.find(a => a.is_default) || user.addresses[0];
      setSelectedAddress(defaultAddr?.id || null);
    }
  }, [user?.addresses, selectedAddress]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    title: '',
    address: '',
    district: '',
    city: '',
    is_default: false,
    latitude: null,
    longitude: null
  });
  const [gettingLocation, setGettingLocation] = useState(false);

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum desteklemiyor');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNewAddress(prev => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success('Konum alındı!');
        setGettingLocation(false);
      },
      (err) => {
        toast.error('Konum alınamadı. Lütfen konum iznini verin.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleAddAddress = async () => {
    if (!newAddress.title || !newAddress.address || !newAddress.district || !newAddress.city) {
      toast.error(txt.fillAllFields);
      return;
    }
    
    try {
      const result = await addAddress(newAddress);
      // Auto-select the newly added address
      if (result?.address?.id) {
        setSelectedAddress(result.address.id);
      }
      toast.success(txt.addressAdded);
      setShowAddressDialog(false);
      setNewAddress({ title: '', address: '', district: '', city: '', is_default: false, latitude: null, longitude: null });
    } catch (error) {
      toast.error(txt.addressError);
    }
  };

  const handleSubmitOrder = async () => {
    // Misafir siparişi için
    if (isGuestCheckout) {
      setLoading(true);
      try {
        const orderData = {
          items: items.map(item => ({
            item_id: item.item_id,
            name: item.name,
            name_tr: item.name_tr || item.name,
            quantity: item.quantity,
            price: item.price,
            item_type: item.item_type,
            catering_selections: item.catering_selections || null
          })),
          table_number: parseInt(tableNumber),
          guest_name: guestName || 'Misafir',
          guest_phone: '',
          payment_method: paymentMethod,
          notes: notes || null,
          is_guest_order: true
        };

        const response = await axios.post(`${API}/orders/guest`, orderData);

        setOrderSubmitted(true);
        toast.success(txt.orderSuccess);
        navigate(`/order-confirmation?order=${response.data.id}&table=${tableNumber}`);
        clearCart();
      } catch (error) {
        console.error('Guest order error:', error);
        const errorMessage = error.response?.data?.detail || txt.orderError;
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Normal kullanıcı siparişi
    const storedTable = tableNumber || sessionStorage.getItem('tableNumber');
    const isTableOrderForUser = !!storedTable;
    
    if (!isTableOrderForUser && !selectedAddress) {
      toast.error(txt.selectAddress);
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: items.map(item => ({
          item_id: item.item_id,
          name: item.name,
          name_tr: item.name_tr || item.name,
          quantity: item.quantity,
          price: item.price,
          item_type: item.item_type,
          catering_selections: item.catering_selections || null
        })),
        address_id: isTableOrderForUser ? undefined : selectedAddress,
        payment_method: paymentMethod,
        notes: notes || null,
        redeem_points: usePoints ? pointsToUse : 0,
        coupon_code: appliedCoupon || null,
        table_number: isTableOrderForUser ? parseInt(storedTable) : undefined
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrderSubmitted(true);
      toast.success(txt.orderSuccess);
      navigate(`/order-confirmation?order=${response.data.id}`);
      clearCart();
      refreshUser();
    } catch (error) {
      console.error('Order error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || txt.orderError;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals with coupon and points
  const pointsDiscount = usePoints ? pointsToUse * EURO_PER_POINT : 0;
  const afterCoupon = totalPrice - couponDiscount;
  const finalTotal = Math.max(0, afterCoupon - pointsDiscount);
  const pointsEarned = Math.floor(finalTotal * 0.1); // 1 point per €10 spent

  // Handle points toggle
  const handleUsePointsToggle = (checked) => {
    setUsePoints(checked);
    if (checked && loyaltyPoints >= MIN_POINTS_TO_REDEEM) {
      // Default to using all available points up to order total
      const maxUsablePoints = Math.floor(totalPrice / EURO_PER_POINT);
      setPointsToUse(Math.min(loyaltyPoints, maxUsablePoints));
    } else {
      setPointsToUse(0);
    }
  };

  if (items.length === 0 && !orderSubmitted) {
    navigate('/cart');
    return null;
  }

  const closedBanner = restaurantClosed ? (
    <div className="bg-red-500 text-white py-3 px-4 text-center font-medium" data-testid="closed-banner">
      <Clock className="w-5 h-5 inline mr-2" />
      {closedMessage || 'Şu an kapalıyız'}
    </div>
  ) : null;

  // Misafir (Masa) siparişi için farklı UI
  if (isGuestCheckout) {
    return (
      <div className="min-h-screen bg-bone pb-8">
        {closedBanner}
        {/* Header */}
        <div className="bg-charcoal py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center">
                  <span className="font-bold text-white">{tableNumber}</span>
                </div>
                <span className="text-white/80">{txt.tableOrder}</span>
              </div>
              <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-2">
                {txt.guestTitle}
              </h1>
              <p className="text-white/60">{txt.guestSubtitle}</p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Guest Info & Payment */}
            <div className="lg:col-span-2 space-y-6">
              {/* Guest Info */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-basil" />
                  </div>
                  <h2 className="font-playfair text-xl font-semibold text-charcoal">{txt.guestInfo}</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>{txt.guestName}</Label>
                    <Input
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder={txt.guestNamePlaceholder}
                      className="mt-1"
                      data-testid="guest-name-input"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-terracotta" />
                  </div>
                  <h2 className="font-playfair text-xl font-semibold text-charcoal">{txt.paymentMethod}</h2>
                </div>

                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'nakit' ? 'border-terracotta bg-terracotta/5' : 'border-stone-200 hover:border-stone-300'}`}>
                      <RadioGroupItem value="nakit" />
                      <Banknote className="w-6 h-6 text-charcoal/70" />
                      <div>
                        <span className="font-medium text-charcoal">{txt.payAtTable}</span>
                        <p className="text-sm text-charcoal/60">{txt.payAtTableDesc}</p>
                      </div>
                    </label>
                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'kart' ? 'border-terracotta bg-terracotta/5' : 'border-stone-200 hover:border-stone-300'}`}>
                      <RadioGroupItem value="kart" />
                      <CreditCard className="w-6 h-6 text-charcoal/70" />
                      <div>
                        <span className="font-medium text-charcoal">{txt.cardOnDelivery}</span>
                        <p className="text-sm text-charcoal/60">{txt.cardDesc}</p>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </motion.div>

              {/* Notes */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <Label className="text-charcoal font-medium">{txt.orderNote}</Label>
                <Textarea
                  placeholder={txt.orderNotePlaceholder}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-2"
                  data-testid="guest-order-notes-input"
                />
              </motion.div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">{txt.orderSummary}</h2>
                
                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div key={`${item.item_id}-${item.item_type}`} className="flex justify-between">
                      <div>
                        <p className="text-charcoal font-medium">{item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x {item.name}</p>
                      </div>
                      <span className="text-charcoal font-medium">€{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-200 pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-charcoal">{txt.total}</span>
                    <span className="font-playfair text-2xl font-bold text-terracotta">€{totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={handleSubmitOrder}
                  disabled={loading || restaurantClosed}
                  className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6 text-base font-semibold mt-6"
                  data-testid="guest-submit-order-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : restaurantClosed ? (
                    <>{closedMessage || 'Şu an kapalıyız'}</>
                  ) : (
                    <>
                      {txt.confirmOrder}
                      <Check className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pb-8">
      {closedBanner}
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-2">
              {txt.pageTitle}
            </h1>
            <p className="text-charcoal/60">
              {txt.pageSubtitle}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Address & Payment */}
          <div className="lg:col-span-2 space-y-6">
            {/* Address Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-basil" />
                  </div>
                  <h2 className="font-playfair text-xl font-semibold text-charcoal">
                    {txt.deliveryAddress}
                  </h2>
                </div>
                <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="rounded-full"
                      data-testid="add-address-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {txt.newAddress}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{txt.addNewAddress}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>{txt.addressTitle}</Label>
                        <Input
                          placeholder={txt.addressTitlePlaceholder}
                          value={newAddress.title}
                          onChange={(e) => setNewAddress({ ...newAddress, title: e.target.value })}
                          className="mt-1"
                          data-testid="address-title-input"
                        />
                      </div>
                      <div>
                        <Label>{txt.addressDetail}</Label>
                        <Textarea
                          placeholder={txt.addressDetailPlaceholder}
                          value={newAddress.address}
                          onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                          className="mt-1"
                          data-testid="address-detail-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{txt.district}</Label>
                          <Input
                            placeholder={txt.district}
                            value={newAddress.district}
                            onChange={(e) => setNewAddress({ ...newAddress, district: e.target.value })}
                            className="mt-1"
                            data-testid="address-district-input"
                          />
                        </div>
                        <div>
                          <Label>{txt.city}</Label>
                          <Input
                            placeholder={txt.city}
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                            className="mt-1"
                            data-testid="address-city-input"
                          />
                        </div>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={getLocation}
                          disabled={gettingLocation}
                          className={`w-full mt-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${newAddress.latitude ? 'border-green-500 bg-green-50 text-green-700' : 'border-stone-200 text-charcoal/60 hover:border-basil'}`}
                          data-testid="get-location-btn"
                        >
                          <MapPin className="w-4 h-4" />
                          {gettingLocation ? 'Konum alınıyor...' : newAddress.latitude ? 'Konum Alındı' : 'Konumumu Al'}
                        </button>
                        {newAddress.latitude && (
                          <p className="text-xs text-green-600 mt-1 text-center">{newAddress.latitude.toFixed(5)}, {newAddress.longitude.toFixed(5)}</p>
                        )}
                      </div>
                      <Button 
                        onClick={handleAddAddress} 
                        className="w-full bg-basil text-white hover:bg-basil/90"
                        data-testid="save-address-btn"
                      >
                        {txt.saveAddress}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {user?.addresses?.length > 0 ? (
                <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress}>
                  <div className="space-y-3">
                    {user.addresses.map((addr) => (
                      <label
                        key={addr.id}
                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedAddress === addr.id 
                            ? 'border-basil bg-basil/5' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <RadioGroupItem value={addr.id} className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-charcoal">{addr.title}</span>
                            {addr.is_default && (
                              <span className="text-xs bg-basil/10 text-basil px-2 py-0.5 rounded-full">
                                {txt.default}
                              </span>
                            )}
                          </div>
                          <p className="text-charcoal/70 text-sm">
                            {addr.address}
                          </p>
                          <p className="text-charcoal/60 text-sm">
                            {addr.district}, {addr.city}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </RadioGroup>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-charcoal/30 mx-auto mb-4" />
                  <p className="text-charcoal/60">{txt.noAddress}</p>
                </div>
              )}
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-terracotta" />
                </div>
                <h2 className="font-playfair text-xl font-semibold text-charcoal">
                  {txt.paymentMethod}
                </h2>
              </div>

              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="space-y-3">
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'nakit' 
                        ? 'border-terracotta bg-terracotta/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <RadioGroupItem value="nakit" />
                    <Banknote className="w-6 h-6 text-charcoal/70" />
                    <div>
                      <span className="font-medium text-charcoal">{txt.cashOnDelivery}</span>
                      <p className="text-sm text-charcoal/60">{txt.cashDesc}</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'kart' 
                        ? 'border-terracotta bg-terracotta/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <RadioGroupItem value="kart" />
                    <CreditCard className="w-6 h-6 text-charcoal/70" />
                    <div>
                      <span className="font-medium text-charcoal">{txt.cardOnDelivery}</span>
                      <p className="text-sm text-charcoal/60">{txt.cardDesc}</p>
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </motion.div>

            {/* Notes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <Label className="text-charcoal font-medium">{txt.orderNote}</Label>
              <Textarea
                placeholder={txt.orderNotePlaceholder}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
                data-testid="order-notes-input"
              />
            </motion.div>

            {/* Loyalty Points */}
            {loyaltyPoints >= MIN_POINTS_TO_REDEEM && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-basil/10 to-basil/5 rounded-2xl p-6 border-2 border-basil/20"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-basil rounded-full flex items-center justify-center">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-charcoal">{txt.usePoints}</h3>
                      <p className="text-sm text-charcoal/60">{loyaltyPoints} {txt.pointsAvailable}</p>
                    </div>
                  </div>
                  <Switch
                    checked={usePoints}
                    onCheckedChange={handleUsePointsToggle}
                    data-testid="use-points-switch"
                  />
                </div>
                
                {usePoints && (
                  <div className="mt-4 pt-4 border-t border-basil/20">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-charcoal">{txt.pointsToUse}</Label>
                      <span className="text-basil font-semibold">
                        -{(pointsToUse * EURO_PER_POINT).toFixed(2)} €
                      </span>
                    </div>
                    <Input
                      type="number"
                      value={pointsToUse}
                      onChange={(e) => {
                        const val = Math.min(
                          Math.max(MIN_POINTS_TO_REDEEM, parseInt(e.target.value) || 0),
                          loyaltyPoints,
                          Math.floor(totalPrice / EURO_PER_POINT)
                        );
                        setPointsToUse(val);
                      }}
                      min={MIN_POINTS_TO_REDEEM}
                      max={Math.min(loyaltyPoints, Math.floor(totalPrice / EURO_PER_POINT))}
                      className="bg-white"
                      data-testid="points-input"
                    />
                    <p className="text-xs text-charcoal/60 mt-2">
                      {txt.minPoints} {MIN_POINTS_TO_REDEEM} {txt.pointsInfo}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                {txt.orderSummary}
              </h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={`${item.item_id}-${item.item_type}`} className="flex justify-between">
                    <div>
                      <p className="text-charcoal font-medium">
                        {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x {item.name}
                      </p>
                      <p className="text-charcoal/50 text-sm">
                        {item.item_type === 'catering' ? txt.catering : txt.alakart}
                      </p>
                    </div>
                    <span className="text-charcoal font-medium">
                      €{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-200 pt-4 space-y-3">
                <div className="flex justify-between text-charcoal/70">
                  <span>{txt.subtotal}</span>
                  <span>€{totalPrice.toFixed(2)}</span>
                </div>
                {appliedCoupon && couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      {txt.couponDiscount} ({appliedCoupon})
                    </span>
                    <span>-€{couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                {usePoints && pointsDiscount > 0 && (
                  <div className="flex justify-between text-basil">
                    <span className="flex items-center gap-1">
                      <Gift className="w-4 h-4" />
                      {txt.pointsDiscount}
                    </span>
                    <span>-€{pointsDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-charcoal/70">
                  <span>{txt.delivery}</span>
                  <span className="text-basil">{txt.free}</span>
                </div>
                <div className="border-t border-stone-200 pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-charcoal">{txt.total}</span>
                    <span className="font-playfair text-2xl font-bold text-terracotta">
                      €{finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Points to earn */}
                <div className="flex items-center justify-between pt-2 text-sm">
                  <span className="text-charcoal/60 flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    {txt.pointsEarned}
                  </span>
                  <span className="text-basil font-semibold">+{pointsEarned} puan</span>
                </div>
              </div>

              <Button
                onClick={handleSubmitOrder}
                disabled={loading || (!(tableNumber || sessionStorage.getItem('tableNumber')) && !selectedAddress) || restaurantClosed}
                className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6 text-base font-semibold mt-6"
                data-testid="submit-order-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : restaurantClosed ? (
                  <>{closedMessage || 'Şu an kapalıyız'}</>
                ) : (
                  <>
                    {txt.confirmOrder}
                    <Check className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
